export function transformOrders(orderData) {
  return orderData.map((order) => {
    // Calculate total price including variants
    const totalPrice = order.order_item.reduce((total, item) => {
      // Base item price
      let itemTotal = item.unit_price * item.quantity;
      
      // Add variant prices if they exist - multiply by item quantity
      if (item.custom_variant && item.custom_variant.length > 0) {
        const variantTotal = item.custom_variant?.reduce((variantSum, variant) => {
          return variantSum + (parseFloat(variant.unit_price) * variant.option_quantity);
        }, 0);
        // Apply variant total to each unit of the main item
        itemTotal += variantTotal * item.quantity;
      }
      
      return total + itemTotal;
    }, 0);

    return {
      id: order.order_id,
      restaurant: order.restaurant_name_en,
      date: formatDate(order.order_date),
      items: order.order_item.map((item) => {
        // Create base item name without variants (since they'll be displayed separately)
        let itemName = item.menu_item.name_en;
        
        // Calculate base item price without variants
        let baseItemPrice = item.unit_price * item.quantity;

        return {
          name: itemName,
          price: formatCurrency(baseItemPrice, order.currency_id),
          quantity: item.quantity,
          variants: item.custom_variant ? item.custom_variant.map(variant => ({
            name: variant.name_en,
            price: formatCurrency(parseFloat(variant.unit_price) * variant.option_quantity * item.quantity, order.currency_id),
            quantity: variant.option_quantity
          })) : []
        };
      }),
      // Use the server-provided total price if available, otherwise use calculated total
      total: order.order_total_price ? formatCurrency(parseFloat(order.order_total_price), order.currency_id) : formatCurrency(totalPrice, order.currency_id),
      status: order.order_status,
      notes: order.comments,
      branch_id: order.branch_id,
      branch_name: order.branch_name_en,
      restaurant_id: order.restaurant_id,
      service_type: order.service_type,
      address_id: order.address_id,
      delivery_charges: order.delivery_charges,
      order_no: order.order_no,
    };
  });
}

function formatDate(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-GB"); // Converts to DD/MM/YYYY format
}

function formatCurrency(amount, currencyId) {
  const currencySymbol = getCurrencySymbol(currencyId); // Replace with actual function
  return `${currencySymbol} ${amount.toFixed(3)}`;
}

// function mapOrderStatus(status) {
//   const statusMap = {
//     Accepted: "Preparing",
//     Canceled: "Re-Order",
//     Delivered: "Completed",
//   };
//   return statusMap[status] || status;
// }

function getCurrencySymbol(currencyId) {
  const currencyMap = {
    3: "KWD",
  };
  return currencyMap[currencyId] || "";
}

export const checkoutPayload = (orderData) => {
  const {
    customerId,
    serviceType,
    addressId,
    total,
    subtotal,
    deliveryCharges,
    restaurantId,
    branchId,
    comments,
    orderType,
    schedule,
    promo_code,
  } = orderData;

  // Base payload
  const payload = {
    customer_id: customerId,
    service_type: serviceType,
    restaurant_id: restaurantId,
    payment_method_id: 1,
    currency_id: "3", // Assuming this is static
    country_id: "3", // Assuming this is static
    driver_id: "0", // Assuming this is static
    tip_id: 0,
    tip_amount: 0,
    store_pickup: "0", // Assuming this is static
    car_pickup: "0", // Assuming this is static

    branch_id: branchId,
    address_id: addressId,
    order_total_price: total,
    order_sub_total_price: subtotal,
    delivery_charges: serviceType === "delivery" ? deliveryCharges : 0,
    promo_code: promo_code || "",
    comments: comments || "",
    order_type: orderType || "asap", // Include order type
    discount_price: "0.000", // Assuming this is static
    delivery_schedule: {
      date: "",
      from_time: "",
      to_time: "",
    }
  };

  // Add schedule data if present
  if (orderType === "schedule" && schedule) {
    payload.delivery_schedule = {
      date: schedule.date,
      from_time: schedule.from_time,
      to_time: schedule.to_time,
    };
    payload.schedule_later = "1"; // Assuming this is static
  } else {
    payload.schedule_later = "0"; // Assuming this is static
  }

  return payload;
};

/**
 * Parses a time string in HH:MM format and returns the total minutes
 * @param {string} timeStr - Time in HH:MM format (e.g. "12:30")
 * @param {string} timeType - "from_time" or "to_time" to handle overnight times
 * @returns {number} Total minutes
 */
export const parseTimeToMinutes = (timeStr, timeType = '') => {
  if (!timeStr) return 0;
  
  const [hours, minutes] = timeStr.split(':').map(Number);
  
  let totalMinutes = hours * 60 + minutes;
  
  // Handle cases where to_time is past midnight (e.g., "01:45" meaning 1:45 AM the next day)
  // If it's a to_time less than 6:00 AM, assume it's past midnight
  if (timeType === 'to_time' && hours < 6) {
    totalMinutes += 24 * 60; // Add 24 hours
  }
  
  return totalMinutes;
};

/**
 * Converts minutes to HH:MM AM/PM format
 * @param {number} totalMinutes - Time in minutes
 * @returns {string} Time in HH:MM AM/PM format
 */
export const formatMinutesToTime = (totalMinutes) => {
  // Normalize minutes to 24-hour day if needed
  totalMinutes = totalMinutes % (24 * 60);
  
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  
  // Convert to 12-hour format
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12; // Convert 0 to 12 for 12 AM
  
  return `${displayHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${period}`;
};

/**
 * Generates time slots based on branch hours and current time
 * @param {Array} branchHours - Array of branch hours with day-specific objects
 * @param {string} selectedDate - Selected date in YYYY-MM-DD format
 * @param {string} today - Today's date in YYYY-MM-DD format
 * @returns {Array} Array of time slot objects
 */
export const generateTimeSlots = (branchHours, selectedDate, today) => {
  const slots = [];

  if (!branchHours || !selectedDate) {
    return slots;
  }

  // Get day of week for the selected date
  const selectedDateObj = new Date(selectedDate + "T00:00:00");
  const dayOfWeek = selectedDateObj.toLocaleDateString('en-US', { weekday: 'long' });
  
  // Find the branch hours for the selected day
  const dayHours = branchHours.find(day => day.week_day === dayOfWeek);

  // Get from and to time in minutes
  let fromMinutes = dayHours?.from_time 
    ? parseTimeToMinutes(dayHours.from_time, 'from_time')
    : 9 * 60; // Default to 9:00 AM
    
  let toMinutes = dayHours?.to_time
    ? parseTimeToMinutes(dayHours.to_time, 'to_time')
    : 22 * 60; // Default to 10:00 PM
  
  // If to_time is earlier than from_time, it means it's past midnight
  // Add 24 hours to represent next day
  if (toMinutes < fromMinutes) {
    toMinutes += 24 * 60;
  }

  // Check if selected date is today or in the future
  const now = new Date();
  const todayObj = new Date(today + "T00:00:00");

  // If dates are earlier than today, return empty slots
  if (selectedDateObj < todayObj) {
    return slots;
  }

  // If it's today, adjust fromMinutes based on current time
  const isToday = selectedDate === today;
  if (isToday) {
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTimeInMinutes = currentHour * 60 + currentMinute;
    
    // For today, we need special handling for past-midnight slots
    // Convert currentTimeInMinutes to 0-24 hour scale for comparison
    const currentTimeNormalized = currentTimeInMinutes;
    
    // For each potential slot, check if it's in the past for today
    const generateValidSlots = () => {
      const validSlots = [];
      // Generate time slots at 1-hour intervals
      const interval = 60; // 60 minutes interval
      
      for (let minute = fromMinutes; minute < toMinutes; minute += interval) {
        // Convert to 0-24 hour normalized time for comparison
        const slotTimeNormalized = minute % (24 * 60);
        
        // Skip slot if it's in the past for today
        // Add 30-minute buffer for preparation time
        if (isToday && slotTimeNormalized <= currentTimeNormalized + 30) {
          continue;
        }
        
        // Check if this is the last slot
        const nextMinute = minute + interval;
        const isLastSlot = nextMinute >= toMinutes;
        
        const startTime = formatMinutesToTime(minute);
        const endTime = isLastSlot ? formatMinutesToTime(toMinutes) : formatMinutesToTime(nextMinute);
        
        // Calculate normalized hour for sorting (0-24)
        const normalizedHour = Math.floor(slotTimeNormalized / 60);
        
        validSlots.push({
          label: `${startTime} - ${endTime}`,
          value: `${startTime} - ${endTime}`,
          hour: Math.floor(minute / 60),
          minute: minute % 60,
          normalizedHour, // Add for sorting
          isClosed: dayHours?.openingStatus !== "Open" // Add flag for closed status
        });
      }
      return validSlots;
    };
    
    // Generate valid slots (filtering out past slots for today)
    const validSlots = generateValidSlots();
    
    // Sort slots to show midnight/early morning slots first
    validSlots.sort((a, b) => {
      const aHour = a.normalizedHour;
      const bHour = b.normalizedHour;
      
      // If one is early morning (0-5:59) and the other isn't
      if (aHour < 6 && bHour >= 6) return -1;
      if (bHour < 6 && aHour >= 6) return 1;
      
      // If both are in the same category, sort them chronologically
      return aHour - bHour;
    });
    
    return validSlots;
  }
  
  // If it's a future date, generate all slots without filtering by current time
  // Generate time slots at 1-hour intervals
  const interval = 60; // 60 minutes interval
  for (let minute = fromMinutes; minute < toMinutes; minute += interval) {
    // Check if this is the last slot
    const nextMinute = minute + interval;
    const isLastSlot = nextMinute >= toMinutes;
    
    const startTime = formatMinutesToTime(minute);
    const endTime = isLastSlot ? formatMinutesToTime(toMinutes) : formatMinutesToTime(nextMinute);
    
    // Calculate normalized hour for sorting (0-24)
    const normalizedHour = Math.floor((minute % (24 * 60)) / 60);
    
    slots.push({
      label: `${startTime} - ${endTime}`,
      value: `${startTime} - ${endTime}`,
      hour: Math.floor(minute / 60),
      minute: minute % 60,
      normalizedHour, // Add for sorting
      isClosed: dayHours?.openingStatus !== "Open" // Add flag for closed status
    });
  }

  // Sort slots to show midnight/early morning slots first, then the rest in chronological order
  // Slots between midnight (0) and 6 AM come first, then all other slots
  slots.sort((a, b) => {
    const aHour = a.normalizedHour;
    const bHour = b.normalizedHour;
    
    // If one is early morning (0-5:59) and the other isn't
    if (aHour < 6 && bHour >= 6) return -1;
    if (bHour < 6 && aHour >= 6) return 1;
    
    // If both are in the same category (both early morning or both daytime)
    // Sort them chronologically within their category
    return aHour - bHour;
  });

  return slots;
};

/**
 * Checks if a time slot should be disabled
 * @param {object} slot - Time slot with hour and minute properties
 * @param {string} selectedDate - Selected date in YYYY-MM-DD format
 * @param {string} today - Today's date in YYYY-MM-DD format
 * @returns {boolean} True if slot should be disabled
 */
export const isTimeSlotDisabled = (slot, selectedDate, today) => {
  // If not today, enable all slots
  if (!selectedDate || selectedDate !== today) {
    return false;
  }

  const now = new Date();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  const currentTimeInMinutes = currentHour * 60 + currentMinute;
  const slotTimeInMinutes = slot.hour * 60 + (slot.minute || 0);
  
  // Disable if slot's time is less than current time plus a 30-minute buffer
  return slotTimeInMinutes <= currentTimeInMinutes + 30;
};
